export default {
  header: {
    notifications: 'Notifications',
    language: 'Language',
    zhCN: 'Chinese (Simplified)',
    en: 'English',
    newChat: 'New Chat',
  },
  headerNotification: {
    title: 'Notifications & Alerts',
    noUnread: 'No unread messages',
    unreadSummary: '{count} unread messages',
    emptyTab: 'No messages in this category',
    viewAll: 'View all notifications',
    sourceXiaoyuClaw: 'XiaoyuClaw',
  },
  common: {
    ok: 'OK',
    confirm: 'Confirm',
    cancel: 'Cancel',
    delete: 'Delete',
    times: 'times',
    collapse: 'Collapse',
    expand: 'Expand',
    loading: 'Loading...',
    noData: 'No Data',
    search: 'Search',
    experimental: 'Experimental',
  },
  platformNav: {
    workspace: 'Workspace',
    assistant: 'Assistant',
    experts: 'AI Employees',
    resources: 'Resources',
    skills: 'Skills',
    connectors: 'Connectors',
    knowledge: 'Knowledge',
    automation: 'Automation',
    apps: 'Apps',
    dev: 'AI Dev',
    comingSoon: 'This module is under construction and will be improved in upcoming releases.',
    help: 'Help',
  },
  devPage: {
    subtitle: 'Open Data Hub and Agent Studio for development work.',
    datahub: 'Data Hub',
    datahubDesc: 'Manage schemas, tables, and business data.',
    studio: 'Agent Studio',
    studioDesc: 'Orchestrate agents, models, and runtime settings.',
    noAccess: 'Your account has no access to developer tools. Contact an administrator.',
    back: 'Back',
    exit: 'Exit',
    connecting: 'Connecting',
    connectingHint: 'Opening the embedded app.',
    embedTimeout: 'Timed out. Exit and try again.',
  },
  resourcesPage: {
    subtitle: 'Manage skills, connectors, and knowledge in one place.',
    tabs: {
      skills: 'Skills',
      connectors: 'Connectors',
      knowledge: 'Knowledge',
    },
  },
  automation: {
    subtitle: 'Let assistants, AI employees, or scripts execute tasks on a schedule.',
    create: 'New automation',
    createDesc: 'Choose an executor, write the task prompt, and set the schedule.',
    tabs: {
      assistant: 'Assistant',
      expert: 'AI Employee',
      script: 'Script',
      assistantHint: 'Assistant automations run as OpenClaw cron jobs inside your instance.',
      expertHint: 'AI employee automations are scheduled by the platform and report back asynchronously.',
      scriptHint: 'Script automations run on a cron schedule in one-off isolated containers — no instance required.',
    },
    running: 'Running',
    total: 'Total tasks',
    executedTotal: 'Total runs',
    monthlyExecutions: 'This month',
    successRate: 'Success rate',
    columns: {
      taskName: 'Task name',
      lastRunTime: 'Last run time',
      executedCount: 'Executions',
      abnormalCount: 'Errors',
      status: 'Status',
      actions: 'Actions',
      detail: 'Run details',
      agentName: 'Expert',
      nextRun: 'Next run',
      content: 'Content',
    },
    executorBadge: {
      assistant: 'Assistant',
      script: 'Script',
    },
    viewDetail: 'View details',
    noLastRun: 'No runs yet',
    noAbnormalData: '0',
    empty: {
      assistant: 'No assistant automations yet. Click "New automation" to create one.',
      expert: 'No expert automations yet. Click "New automation" to create one.',
      script: 'No script automations yet. Click "New automation" to create one.',
    },
    status: {
      running: 'Running',
      completed: 'Completed',
      paused: 'Paused',
      waiting: 'Waiting',
      terminated: 'Terminated',
      abnormal: 'Abnormal',
      humanIntervention: 'Needs intervention',
    },
    actions: {
      pauseRun: 'Pause',
      resumeRun: 'Resume',
      stopTask: 'Stop task',
      restartTask: 'Restart task',
      deleteTask: 'Delete task',
      intervene: 'Intervene',
      viewRuns: 'Run history',
      delete: 'Delete',
      pause: 'Pause',
      resume: 'Resume',
    },
    runsTitle: 'Run history: {name}',
    runTime: 'Run time',
    runStatus: 'Status',
    runSummary: 'Summary',
    runDuration: 'Duration',
    noRuns: 'No run history yet',
    scriptUploadFailed: 'Automation created, but the script package upload failed: {reason}',
    runScriptStatus: {
      running: 'Running',
      success: 'Success',
      failed: 'Failed',
      timeout: 'Timeout',
    },
    scriptLog: {
      output: 'Stdout',
      error: 'Stderr',
      empty: 'No logs',
    },
    artifacts: {
      title: 'Artifacts',
      empty: 'No artifacts in this run',
      view: 'View',
      download: 'Download',
      kindReport: 'Report',
      kindFile: 'File',
      badge: '{count} artifacts',
      loadFailed: 'Failed to load artifact: {reason}',
      viewerTitle: 'Artifact: {name}',
    },
  },
  apps: {
    title: 'Apps',
    systemPrompt: 'You are now in Apps developer mode. The user is iteratively building a multi-page HTML/JS/CSS app through natural language. Follow these rules:\n1. The current app is "{appName}" and the current page is "{pageName}". When the user asks to create or update a page, provide the complete, self-contained HTML in a ```html ... ``` code block.\n2. The frontend will automatically save the HTML from the code block to the current page. Do NOT write files to disk and do NOT call the canvas tool.\n3. If the user wants a new page, prompt them to use the "Add page" button on the left.\n4. Each page should be either a complete document or a fragment that can be embedded in a document shell.',
    runtimeSystemPrompt: 'This is a runnable light app "{appName}" (app_id: {appId}) that builds and runs in its own lightweight container. Use your loaded `light-app-dev` skill to develop it: author the complete multi-file project under `workspace/apps/{appId}/`, then call the app_run tool to build and start it in a container. The skill has the full project-structure / manifest / run guidance. When done, briefly summarize the project structure and how to preview it.',
    packageSystemPrompt: 'This is a packaged light app "{appName}" (app_id: {appId}) — a zip-packaged static app. Follow these rules:\n1. Use the write tool to author/edit files under `apps/{appId}/` in your workspace (e.g. index.html, style.css, app.js).\n2. The platform syncs those files into the app bundle — the preview updates. Do NOT reply with just a single HTML block.\n3. Briefly summarize which files you changed.',
    overview: {
      title: 'Apps',
      subtitle: 'Personal apps are built and maintained by you; enterprise apps are published after admin approval.',
      create: 'Create App',
      upload: 'Upload Light App',
      searchPlaceholder: 'Search apps',
      personal: 'Personal Apps',
      org: 'Enterprise Apps',
      reviewing: 'Under Review',
      empty: {
        personal: 'No personal apps yet. Click "Create App" to start.',
        org: 'No enterprise apps yet.',
        reviewing: 'No apps under review.',
      },
    },
    card: {
      open: 'Open App →',
      personalBadge: 'Personal',
      orgBadge: 'Enterprise',
      reviewingBadge: 'Pending',
      packageBadge: 'Package',
      agentBadge: 'Agent',
      pages: '{count} pages',
      version: 'v{version}',
      kindStatic: 'Static',
      kindRuntime: 'Container',
    },
    detail: {
      back: 'Apps',
      share: 'Submit for Review',
      sharePending: 'Under Review',
      vibeCoding: 'Open Vibe Coding',
      vibeCodingClose: 'Close Vibe Coding',
      reupload: 'Upload New Version',
      downloadSource: 'Download Source',
      agentUploadLink: 'Agent Upload Link',
      more: 'More',
      publishPackage: 'Publish as Light App',
    },
    runtime: {
      run: 'Run',
      stop: 'Stop',
      started: 'Runtime container started',
      starting: 'Starting…',
      startFailed: 'Failed to start runtime container: {message}',
      stopped: 'Runtime container stopped',
      stopFailed: 'Failed to stop runtime container: {message}',
      timeout: 'Container start timed out — please retry',
      buildFailed: 'Build failed (exit code {code}) — see build logs',
      status: {
        starting: 'Starting',
        building: 'Building',
        running: 'Running',
        stopped: 'Stopped',
        error: 'Error',
      },
      notRunningHint: 'App is not running — click “Run” in the top right to start',
      buildingHint: 'Building and starting…',
      viewBuildLogs: 'View build logs',
      viewRunLogs: 'View runtime logs',
      buildLogs: 'Build logs',
      runLogs: 'Runtime logs',
      logsEmpty: 'No logs yet',
      logsLoadFailed: 'Failed to load logs',
    },
    editor: {
      open: 'Edit Files',
      close: 'Close Editor',
      openReadonly: 'View Files',
      readonlyBadge: 'Read-only',
      files: 'Files',
      newFile: 'New File',
      newFilePrompt: 'Enter a file path (with extension, e.g. about.html, css/app.css)',
      newFileInvalid: 'File name needs an extension',
      newFolder: 'New Folder',
      newFolderPrompt: 'Enter a folder path (e.g. assets, js)',
      newFolderInvalid: 'Folder path cannot be empty',
      rename: 'Rename',
      renamePrompt: 'Enter the new path',
      delete: 'Delete',
      deleteConfirm: 'Delete "{path}"? This cannot be undone.',
      save: 'Save',
      saved: 'Saved {path}',
      saveFailed: 'Failed to save: {message}',
      created: 'Created {path}',
      renamed: 'Renamed to {path}',
      deleted: 'Deleted {path}',
      loadFailed: 'Failed to load: {message}',
      versions: 'Versions',
      snapshot: 'Save as New Version',
      snapshotPrompt: 'Name the new version (leave empty to auto-increment)',
      snapshotPlaceholder: 'e.g. 1.0.1',
      snapshotted: 'Saved as new version v{version}',
      rollback: 'Rollback',
      rollbackTo: 'Roll back to v{version}',
      rollbackConfirm: 'Roll back to v{version}? The active version switches to v{version}.',
      rolledBack: 'Rolled back to v{version}',
      noSelection: 'No file selected',
      selectHint: 'Select a file on the left to start editing',
      binaryNote: 'Binary file — view only, editing not supported',
      emptyFiles: 'No files',
    },
    address: {
      title: 'Agent Upload Link',
      hint: 'Generate a credential-bearing upload link and copy it to an external agent; it POSTs a zip light app to the link to upload under your account. The link is valid for 1 hour.',
      generate: 'Generate',
      copy: 'Copy',
      copied: 'Copied',
      regenerate: 'Regenerate',
      expiresAt: 'Valid until {time}',
    },
    agentLink: {
      title: 'Agent Upload Link (This App Only)',
      hint: 'Generate an upload link scoped to the current light app. Copy it and hand it to an external agent; uploading a zip to it adds a NEW VERSION of this app and overrides its metadata (name / description) from app.json. Valid for 1 hour.',
      generate: 'Generate',
      copy: 'Copy',
      copied: 'Copied',
      regenerate: 'Regenerate',
      expiresAt: 'Valid until {time}',
    },
    pageList: {
      title: 'Pages',
      add: 'Add Page',
      rename: 'Rename',
      delete: 'Delete',
      empty: 'No pages yet',
      importPaste: 'Paste HTML',
      importFile: 'Import from HTML file',
      importPastePrompt: 'Paste the full HTML (including <!DOCTYPE> or <html>)',
      importPasteEmpty: 'HTML cannot be empty',
    },
    preview: {
      browserUrl: 'Page URL',
      empty: 'Select a page to preview',
      refresh: 'Refresh',
      fullscreen: 'Fullscreen',
      exitFullscreen: 'Exit fullscreen',
      openFull: 'Open full page',
    },
    fullView: {
      notFound: 'App not found or access denied',
      empty: 'This page has no content yet — generate it in Vibe Coding first',
    },
    dialog: {
      createTitle: 'Create App',
      createPlaceholder: 'Enter app name',
      renameTitle: 'Rename App',
      renamePlaceholder: 'Enter new app name',
      pageCreateTitle: 'Add Page',
      pageCreatePlaceholder: 'Enter page name',
      pageRenameTitle: 'Rename Page',
      pageRenamePlaceholder: 'Enter new page name',
    },
    confirm: {
      deleteApp: 'Are you sure you want to delete app "{name}"? This cannot be undone.',
      deletePage: 'Are you sure you want to delete page "{name}"? This cannot be undone.',
      publishPackage: 'Publish "{name}" as a light app? The original app is kept; a new packaged light app is created.',
    },
    toast: {
      created: 'App "{name}" created',
      createFailed: 'Failed to create app: {message}',
      renamed: 'App renamed to "{name}"',
      renameFailed: 'Failed to rename app: {message}',
      deleted: 'App "{name}" deleted',
      deleteFailed: 'Failed to delete app: {message}',
      shared: 'App "{name}" submitted for review',
      shareFailed: 'Failed to submit for review: {message}',
      pageCreated: 'Page "{name}" created',
      pageCreateFailed: 'Failed to create page: {message}',
      pageRenamed: 'Page renamed to "{name}"',
      pageRenameFailed: 'Failed to rename page: {message}',
      pageDeleted: 'Page "{name}" deleted',
      pageDeleteFailed: 'Failed to delete page: {message}',
      pageSaveFailed: 'Failed to save page: {message}',
      pageRenderFailed: 'Failed to render page: {message}',
      pageImported: 'Page "{name}" imported',
      loadFailed: 'Failed to load: {message}',
      uploaded: 'Micro-app "{name}" uploaded',
      uploadedWithWarnings: 'Uploaded with warnings: {warnings}',
      versionUploaded: 'App "{name}" updated to v{version}',
      uploadFailed: 'Failed to upload light app: {message}',
      linkGenerated: 'Upload link generated and copied',
      linkGenerateFailed: 'Failed to generate upload link: {message}',
      publishedAsPackage: 'Published "{name}" as a light app',
      publishFailed: 'Failed to publish as light app: {message}',
      workspaceSynced: 'Workspace files synced to the app',
    },
  },
  expertsPage: {
    subtitle: 'Professional agents configured for your role by the enterprise.',
    searchPlaceholder: 'Search experts',
    favorite: 'Favorite',
    favorited: 'Favorited',
    like: 'Like',
    liked: 'Liked',
    orgConfigured: 'Enterprise configured',
    usesCount: '{count} uses',
    noRating: 'N/A',
    rate: 'Rate',
    rated: 'Rated',
    startChat: 'Start chat →',
    empty: 'No matching experts found',
    noDescription: 'No description available',
    externalAgentTip: 'This expert is external. Please scan the QR code to access.',
    startChatFailed: 'Failed to open expert chat. Please try again.',
    favoriteFailed: 'Failed to update favorite. Please try again.',
    likeFailed: 'Failed to update like. Please try again.',
    tabs: {
      all: 'All',
      favorite: 'Favorites',
    },
    sortLabel: 'Sort',
    sort: {
      createTime: 'Created',
      lastChatTime: 'Last used',
      usageCount: 'Usage count',
    },
  },
  assistantPage: {
    title: 'Enterprise AI Assistant',
    subtitle: 'Your personal Claw · can access files, skills and enterprise resources',
    notLoggedIn: 'Please log in first to use the assistant.',
    instanceStarting: 'Starting, please wait…',
    loadingOlder: 'Loading older messages…',
    emptyTitle: 'What can I help you with?',
    emptyHint: 'Describe the work you want to get done. The assistant will use authorized knowledge, skills and connectors as needed.',
    inputPlaceholder: 'Type a task or question. Enter to send, Shift + Enter for new line',
    skill: 'Skill',
    knowledge: 'Knowledge',
    personalKnowledge: 'Personal Knowledge Base',
    orgKnowledge: 'Enterprise Knowledge Base',
    connector: 'Connector',
    addResource: 'Add resource',
    skillsTab: 'Skills',
    connectorsTab: 'Connectors',
    use: 'Use',
    enabled: 'Enabled',
    disabled: 'Disabled',
    enable: 'Enable',
    disable: 'Disable',
    noSkills: 'No skills',
    noConnectors: 'No connectors',
    modelPlaceholder: 'Select model',
    skillLabel: 'Use skills:',
    attachFile: 'Select file from local',
    stop: 'Stop',
    send: 'Send',
    disclaimer: 'AI-generated content is for reference only. Please verify important information.',
    instanceNotReady: 'Instance not ready, rechecking status...',
    instanceNotRunning: 'Instance is not running, unable to send message',
    stopped: 'Stopped',
    retrying: 'Reconnecting network ({attempt}/{max})',
    retryFailed: 'Reconnect failed. Check your connection and try again.',
    resend: 'Resend',
    networkStalled: 'Network connection lost; output stopped. Check your connection and try again.',
    sendFailed: 'Send failed: {reason}',
    turnLimit: 'Too many concurrent turns; wait for the running ones to finish.',
    streamError: 'Stream error',
    noContent: '(no content)',
    thinking: 'Thinking',
    reasoning: 'Reasoning',
    showThinkingTools: 'Show reasoning and tool calls',
    hideThinkingTools: 'Hide reasoning and tool calls',
    skillPrompt: 'Please help me using the "{name}" skill: ',
    createSkillPrompt: 'Please help me create a skill: ',
    toolCalls: 'Tool calls',
    toolRunning: 'Running',
    toolDone: 'Done',
    toolInterrupted: 'Interrupted',
    toolInput: 'Input',
    toolOutput: 'Output',
    requireInstance: 'Sign in and start your assistant instance first',
    attachLabel: 'Attachments (uploaded to the instance for the assistant to read):',
    attachFailed: 'Attachment upload failed: {reason}',
    connectorSyncFailed: 'Connector sync failed: {reason}',
    knowledgeSyncFailed: 'Failed to sync knowledge selection: {reason}',
    knowledgeRetrievalHint:
      'For this turn the user selected knowledge bases (dataset_ids: {ids}). When a question needs their content, call the ragflow_retrieval tool using ONLY these dataset_ids, ground your answer in the retrieved chunks, and cite sources. Skip retrieval for questions unrelated to the knowledge bases.',
    echartsHint:
      'Your replies support chart rendering: when a data comparison, trend, or proportion calls for a chart, you MUST emit one echarts code block in the reply body (a ```echarts fence whose content is a single valid JSON object — the ECharts option with title/xAxis/yAxis/series etc.); the frontend renders it as an interactive chart. Do NOT write HTML chart files via canvas/write tools and then emit [embed] tags — those do not render in the conversation; the echarts code block is the only chart output channel. Keep normal prose outside the block and never split one option across multiple blocks.',
    mentionSearchPlaceholder: 'Search files in the selected knowledge bases…',
    mentionNoKnowledge: 'Select a knowledge base below before @-referencing its files',
    mentionNoFiles: 'No matching files',
    mentionRetrievalHint:
      'The user referenced these knowledge base files in this turn:\n{files}\nBefore answering related questions, call the ragflow_retrieval tool with the matching document_ids (and optionally dataset_ids) to retrieve exactly these files, and ground your answer in the retrieved content.',
  },
  skillsPage: {
    subtitle: 'Use personal and enterprise skills; create via assistant or install local packages.',
    installPackage: 'Install skill package',
    createByAssistant: 'Create skill via assistant',
    scopePersonal: 'Personal',
    scopeOrg: 'Enterprise',
    review: 'Review',
    ratingPlaceholder: 'Rating (coming soon)',
    likePlaceholder: 'Favorite (coming soon)',
    reviewPlaceholder: 'Review (coming soon)',
    mySkills: 'My skills',
    mySkillsHint: 'Available to use and share to admin platform',
    orgSkills: 'Enterprise skills',
    orgSkillsHint: 'Authorized by admin per role',
    reviewing: 'Under review',
    reviewingHint: 'Distributed by role after admin approval',
    mySkillsSection: 'Personal skills generated by assistant',
    orgSkillsSection: 'Functional skills authorized for your role',
    personalAvailable: 'Personal',
    roleAuthorized: 'Role authorized',
    use: 'Use',
    useLoading: 'Preparing...',
    share: 'Share',
    shareSubmitting: 'Submitting...',
    shareSubmitted: 'Submitted for admin review. It will appear in enterprise skills once approved.',
    shareFailed: 'Share failed: {reason}',
    submitted: 'Submitted',
    tabAll: 'All',
    tabInstalled: 'Installed',
    searchPlaceholder: 'Search by name, description or tag',
    install: 'Install',
    installLoading: 'Installing...',
    uninstall: 'Uninstall',
    uninstallLoading: 'Uninstalling...',
    installedBadge: 'Installed',
    versionsCount: '{count} versions',
    downloadsUnit: 'downloads',
    usageUnit: 'uses',
    availablePersonal: 'Personal use',
    availableOrg: 'Enterprise use',
    loading: 'Loading skills...',
    emptyAll: 'No skill packages available',
    emptyInstalled: 'No skills installed in this instance yet',
    emptySearch: 'No matching skills',
    uploadFailed: 'Upload failed: {reason}',
    uploadPendingReview: 'Skill submitted. It will be distributed after admin approval.',
    requireInstance: 'Sign in and start your assistant instance before managing skills.',
    loadDetailFailed: 'Failed to load skill detail: {reason}',
    installFailed: 'Install failed: {reason}',
    uninstallFailed: 'Uninstall failed: {reason}',
    listInstalledFailed: 'Failed to load installed skills; showing all skills only',
    detailTitle: 'Skill detail',
    detailCategory: 'Category',
    detailVersion: 'Version',
    detailAuthor: 'Author',
    detailDownloads: 'Downloads',
    detailCreatedAt: 'Uploaded',
    detailFormat: 'Format',
    detailTags: 'Tags',
    detailInstructions: 'Instructions',
    detailRequirements: 'Requirements',
    detailBins: 'Binaries',
    detailEnv: 'Environment',
    detailPlatforms: 'Platforms',
    detailHomepage: 'Homepage',
    detailShare: 'Copy share link',
    detailShareCopied: 'Copied',
    detailDownload: 'Download package',
    packageTitle: 'Install local skill package',
    packageDesc: 'The skill will be deployed to "My skills" — only you can use it.',
    packageChooseFile: 'Choose skill package',
    packageFileHint: 'Accepts .zip / .tar.gz (multi-select). One archive may contain several skills at any directory depth; name and version are parsed from SKILL.md',
    packageInstall: 'Install skill',
    packageSecurityNote: 'Package structure and security policy are checked before install. The skill will not be shared to the enterprise automatically.',
    packageNoFile: 'Please choose a skill package first',
    packageSuccess: 'Skill package submitted and installing to this instance',
    packageFilesChosen: '{count} file(s) selected',
    packageResultsTitle: 'Parse results',
    packagePartialFailed: 'Some skills failed to register or install — see parse results',
    packageNoneRegistered: 'No skills could be registered',
    personalEmpty: 'No personal skills yet — create one via the assistant or upload a package.',
    orgEmpty: 'Administrator has not authorized any enterprise skills for your role yet.',
    reviewEmpty: 'No skills under review.',
  },
  connectorsPage: {
    subtitle: 'View enterprise-authorized MCP server connectors.',
    orgWorkbench: 'Enterprise Workbench',
    loading: 'Loading connectors...',
    authorized: 'Enterprise authorized',
    viewPermission: 'View permission',
    empty: 'No enterprise connectors available.',
    loadFailed: 'Failed to load connectors: {reason}',
    statusActive: 'Available',
    statusError: 'Unavailable',
    statusDisabled: 'Disabled',
    detailTitle: 'Connector Details',
    detailTransport: 'Transport',
    detailStatus: 'Status',
    detailProvider: 'Provider',
    detailToolCount: 'Tool Count',
    toolsUnit: 'available tools',
    detailCommand: 'Launch Command',
    detailUrl: 'Service URL',
    detailTools: 'Available Tools',
    noTools: 'No tools available',
    loadDetailFailed: 'Failed to load details: {reason}',
  },
  knowledgePage: {
    publicReadonlyTip: 'Public knowledge bases are read-only and can be used in conversations',
    subtitle: 'Manage personal knowledge, consume enterprise public knowledge.',
    create: 'Create knowledge base',
    personalSection: 'My knowledge bases',
    personalSectionHint: 'Upload, view, download and delete files',
    publicSection: 'Public knowledge bases',
    publicSectionHint: 'Configured per role by admin, read-only',
    readonly: 'Read-only',
    loading: 'Loading knowledge bases...',
    personalEmpty: 'No personal knowledge bases yet. Create one above.',
    publicEmpty: 'No public knowledge bases authorized for your role.',
    createFailed: 'Failed to create: {reason}',
    loadFailed: 'Failed to load knowledge bases: {reason}',
    filesLabel: '{count} documents',
    updatedLabel: 'Updated {time}',
    createDialog: {
      title: 'Create knowledge base',
      desc: 'Store personal materials, documents and research.',
      name: 'Name',
      namePlaceholder: 'Enter name',
      description: 'Description',
      descriptionPlaceholder: 'Enter purpose',
      confirm: 'Create',
    },
  },
  automationPage: {
    subtitle: 'Schedule assistants or experts to run tasks automatically.',
    create: 'New automation',
    running: 'Running',
    monthlyExecutions: 'This month',
    successRate: 'Success rate',
    loading: 'Loading automations...',
    empty: 'No automations yet. Create one above.',
    loadFailed: 'Failed to load automations: {reason}',
    createFailed: 'Failed to create automation: {reason}',
    table: {
      name: 'Task',
      lastRun: 'Last run',
      executions: 'Runs',
      exceptions: 'Errors',
      status: 'Status',
      actions: 'Actions',
      details: 'Details',
    },
    statusRunning: 'Running',
    statusCompleted: 'Completed',
    statusPaused: 'Paused',
    statusNeedsInput: 'Needs intervention',
    createDialog: {
      title: 'New automation',
      desc: 'Pick an executor, write the prompt and set the schedule.',
      name: 'Automation name',
      namePlaceholder: 'e.g. Weekly project progress summary',
      executor: 'Executor',
      executorAssistant: 'AI Assistant',
      executorAssistantHint: 'Uses assistant knowledge and tools',
      executorExpert: 'AI Employee',
      executorExpertHint: 'Calls a specific AI employee with its permissions',
      expertSelect: 'Select expert',
      prompt: 'Task prompt',
      promptPlaceholder: 'Describe the task, inputs to read and expected output',
      promptHint: 'Variables like date, user and run time are supported.',
      schedule: 'Schedule',
      scheduleDaily: 'Daily',
      scheduleWeekly: 'Weekly',
      scheduleMonthly: 'Monthly',
      scheduleWorkday: 'Workdays',
      scheduleCustom: 'Custom',
      time: 'Time',
      timezone: 'Timezone',
      startDate: 'Start date',
      notify: 'Notify me when finished',
      previewPrefix: 'Next run:',
      confirm: 'Create automation',
    },
  },
  userSettings: {
    title: 'User settings',
    editContact: 'Edit contact info',
    accountSecurity: 'Account & security',
    defaultName: 'User',
    defaultDepartment: 'No department',
  },
  aside: {
    logoTitle: 'Agent Workspace',
    myAgents: 'My Agents',
    more: 'More',
    collapse: 'Collapse',
    historyConversations: 'History',
    assistantConversations: 'Assistant chats',
    expertConversations: 'Expert chats',
    searchHistoryConversations: 'Search conversations',
    searchAssistantConversations: 'Search assistant conversations',
    searchExpertConversations: 'Search expert chats',
    newAssistantConversation: 'New assistant conversation',
    assistantFiles: 'Assistant files',
    workspaceTitle: 'Assistant files',
    workspaceSubtitle: 'Claw workspace',
    uploadFile: 'Upload file',
    createFolder: 'New folder',
    allFiles: 'All files',
    goUp: 'Back to parent',
    folder: 'Folder',
    loadingFiles: 'Loading…',
    noFiles: 'No files yet',
    folderNamePlaceholder: 'Enter folder name',
    folderNameRequired: 'Folder name is required',
    uploadSuccess: 'Upload successful',
    uploadFailed: 'Upload failed',
    createFolderSuccess: 'Folder created',
    loadFilesFailed: 'Failed to load files',
    preview: 'Preview',
    download: 'Download',
    renameFileTitle: 'Rename',
    renameFilePlaceholder: 'Enter new name',
    renameFileRequired: 'Name is required',
    renameFileSuccess: 'Renamed successfully',
    renameFileFailed: 'Rename failed',
    deleteFileTitle: 'Delete file',
    deleteFileTip: 'Delete "{name}"? This action cannot be undone.',
    deleteFileSuccess: 'Deleted successfully',
    deleteFileFailed: 'Delete failed',
    downloadFailed: 'Download failed',
    previewLoading: 'Loading preview…',
    previewFailed: 'Failed to load preview',
    newExpertConversation: 'Start a chat after choosing an expert',
    selectExpertFirst: 'Please select an expert to chat with first',
    emptyAssistantConversations: 'No assistant conversations yet',
    emptyExpertConversations: 'No expert conversations yet',
    untitledConversation: 'Untitled conversation',
    noSearchResults: 'No matching conversations',
    moreActions: 'More actions',
    past7Days: 'Past 7 days',
    earlier: 'Earlier',
    loadingConversations: 'Loading conversations...',
    emptyConversations: 'No conversations',
    emptyConversationRecords: 'No conversation records',
    myTasks: 'My Tasks',
    loadingTasks: 'Loading tasks...',
    emptyTasks: 'No tasks',
    newConversation: 'New Conversation',
    newTask: 'New Task',
    today: 'Today',
    yesterday: 'Yesterday',
    theDayBeforeYesterday: 'The day before yesterday',
    waiting: 'Waiting',
    running: 'Running',
    paused: 'Paused',
    terminated: 'Terminated',
    completed: 'Completed',
    humanIntervention: 'Human intervention',
    abnormal: 'Abnormal',
    pleaseSelectAgent: 'Please select an agent first',
    agentNotFound: 'Agent not found',
    taskCreateSuccess: 'Task created successfully',
    taskCreateFailed: 'Task creation failed',
    taskCreateFailedReason: 'Task creation failed: {reason}',
    taskCreateFailedRetry: 'Task creation failed, please try again later',
    deleteConversationTitle: 'Delete conversation?',
    deleteConversationTip: 'This action cannot be undone.',
    confirm: 'Confirm',
    cancel: 'Cancel',
    deleteSuccess: 'Deleted successfully',
    deleteFailed: 'Delete failed',
    deleteFailedRetry: 'Delete failed, please try again later',
    pauseTask: 'Pause task',
    resumeTask: 'Resume task',
    stopTaskTitle: 'Stop task',
    stopTaskConfirm: 'Are you sure to stop this task?',
    stopped: 'Task stopped',
    deleteTaskTitle: 'Delete task?',
    deleteTaskTip: 'This action cannot be undone.',
    deleted: 'Task deleted',
    view: 'View',
    pause: 'Pause',
    resume: 'Resume',
    stop: 'Stop',
    delete: 'Delete',
    reset: 'Reset',
    interveneTitle: 'Human Intervention',
    interveneConfirm: 'Are you sure to intervene?',
    interveneStarted: 'Human intervention started, task continues',
    renameEmptyWarn: 'Please enter a conversation name',
    renameSuccess: 'Renamed successfully',
    renameFailed: 'Rename failed',
    renameFailedRetry: 'Rename failed, please try again later',
    pin: 'Pin',
    pinSuccess: 'Pinned successfully',
    pinFailed: 'Pin failed',
    pinFailedRetry: 'Pin failed, please try again later',
    labels: {
      taskName: 'Task Name',
      recurrence: 'Recurrence',
      executedCount: 'Executed Count',
    },
    terminate: 'Terminate',
    restart: 'Restart',
    intervene: 'Intervene',
    rename: 'Rename',
    renameTitle: 'Rename Conversation',
    renamePlaceholder: 'Enter a new conversation name',
  },
  taskDialog: {
    title: 'Create Task',
    submit: 'Submit Task',
    fields: {
      agent: 'Executor',
      name: 'Task Name',
      desc: 'Description',
      prompt: 'Prompt',
      start: 'Start Time',
      recurrence: 'Recurrence',
    },
    labels: {
      repeat: 'Repeat',
      frequency: 'Frequency',
      endsOn: 'Ends On',
    },
    placeholders: {
      agent: 'Select an agent to run this task',
      name: 'Enter a task name',
      desc: 'Optional, extra context or purpose',
      prompt: 'Enter prompt, e.g. hourly energy stats and brief report',
      start: 'Start Time',
      date: 'Select date',
      count: 'Execution count',
    },
    repeat: {
      once: 'None',
      tenMin: 'Every 10 minutes',
      hour: 'Hourly',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      workday: 'Weekdays',
      custom: 'Custom',
    },
    units: {
      minute: 'Minute',
      hour: 'Hour',
      day: 'Day',
      week: 'Week',
      month: 'Month',
    },
    endsOn: {
      never: 'Never',
      date: 'Date',
      count: 'Count',
    },
    rules: {
      agent: 'Please select an executor',
      name: 'Please enter a task name',
      prompt: 'Please enter a prompt',
      start: 'Please select a start time',
    },
    executor: {
      label: 'Executor',
      assistant: {
        title: 'AI Assistant',
        desc: 'Use the assistant\'s available knowledge and tools',
      },
      selectAgent: {
        title: 'AI Employee',
        desc: 'Invoke a specific AI employee and its authorized capabilities',
      },
      script: {
        title: 'Script',
        desc: 'Run your script project on a schedule in an isolated container',
      },
      selectAgentField: 'Select AI Employee',
      noTaskCapableAgent: 'No AI employees available for scheduled tasks',
      noAssistant: 'No AI assistant available',
    },
    automation: {
      submit: 'Create Automation',
      fields: {
        name: 'Automation Name',
        desc: 'Description',
        prompt: 'Task Prompt',
        schedule: 'Schedule',
      },
      placeholders: {
        name: 'e.g. Weekly project progress summary',
        desc: 'Optional context or purpose for this automation',
        prompt: 'Describe what the automation should do, what data to read, and expected output',
        agent: 'Select an expert',
      },
      rules: {
        name: 'Please enter an automation name',
        prompt: 'Please enter a task prompt',
      },
      promptHint: 'Supports variables such as date, user, and run time.',
      schedulePreview: 'Next run: {datetime} ({period})',
      schedulePreviewDatetime: '{year}/{month}/{day} {time}',
      notifyOnComplete: 'Notify me when the run completes',
      script: {
        package: 'Script Package',
        packageChoose: 'Choose zip file',
        packageEmpty: 'No file selected',
        packageHint: 'Upload a zip project package; it is unpacked as the project root where the entry command runs',
        packageRequired: 'Please choose a zip project package',
        packageInvalidType: 'Only .zip project packages are supported',
        packageTooLarge: 'The project package must be smaller than {limit} MB',
        entryCommand: 'Entry Command',
        entryCommandPlaceholder: 'e.g. python main.py or bash run.sh',
        entryCommandRequired: 'Please enter the entry command',
        timeout: 'Timeout (seconds)',
        advanced: 'Advanced options',
        image: 'Image (optional)',
        imagePlaceholder: 'Leave empty to use the default runner image',
      },
    },
    validate: {
      endMode: 'Please choose end mode (date or count)',
      endDate: 'Please select an end date',
      endCount: 'Please enter a valid end count (≥1)',
    },
    frequency: {
      eachN: 'Every {n}',
    },
  },
  agentHeader: {
    defaultSubtitle: 'Your personal assistant · Access files, skills, and enterprise resources',
    defaultCategory: 'Default Category',
    supportsTask: 'Task Capable',
    detail: 'Details',
    newChat: 'New Chat',
    newTask: 'New Task',
    feedback: 'Feedback',
    detailTitle: 'Agent Details',
    version: 'Version',
    currentStatus: 'Status',
    status: {
      available: 'Available',
      disabled: 'Disabled',
      offline: 'Offline',
    },
    intro: 'Introduction',
    noDescription: 'No description',
    taskCapability: 'Task Capability',
    scheduledSupported: 'Scheduled tasks supported',
    scheduledUnsupported: 'Scheduled tasks not supported',
    stats: {
      instantChat: 'Instant Chats:',
      taskExec: 'Task Executions:',
    },
    times: 'times',
  },
  taskDetail: {
    title: 'Execution Results',
    runsRecordTitle: 'Run History',
    runsSubtitle: '{total} runs · Executor: {agent}',
    paginationTotal: '{total} records',
    reportDialog: {
      description: 'Description:',
      content: 'Content:',
      file: 'File:',
      metadata: 'Metadata:',
      status: 'Status:',
      createTime: 'Created at:',
      generateTime: 'Generated at:',
      errorMessage: 'Error Message:',
      empty: 'No report content',
      close: 'Close',
    },
    brief: {
      name: 'Task Name:',
      agentName: 'Agent Name:',
      agentStatus: 'Agent Status:',
      desc: 'Description:',
      prompt: 'Prompt:',
      timeRange: 'Time Range:',
      period: 'Period:',
      executedCount: 'Executed Count:',
      status: 'Status:',
    },
    status: {
      waiting: 'Waiting',
      running: 'Running',
      paused: 'Paused',
      terminated: 'Terminated',
      completed: 'Completed',
      abnormal: 'Abnormal',
      humanIntervention: 'Human Intervention',
    },
    actions: {
      feedback: 'Feedback',
      start: 'Start',
      pause: 'Pause Task',
      stop: 'Stop Task',
      restart: 'Restart Task',
      reset: 'Reset',
      delete: 'Delete Task',
      intervene: 'Human Intervention',
      viewRecord: 'View Conversation',
    },
    confirm: {
      pauseTip: 'Pause this task?',
      stopTip: 'Stop this task? This action cannot be undone.',
      restartTip: 'Restart this task?',
      deleteTip: 'Delete this task? This action cannot be undone.',
      interveneTip: 'Start human intervention?',
    },
    toast: {
      started: 'Task started',
      startFailed: 'Failed to start task',
      startFailedRetry: 'Failed to start task, please try again later',
      paused: 'Task paused',
      pauseFailed: 'Failed to pause task',
      pauseFailedRetry: 'Failed to pause task, please try again later',
      terminated: 'Task terminated',
      stopFailed: 'Failed to stop task',
      stopFailedRetry: 'Failed to stop task, please try again later',
      restarted: 'Task restarted',
      reset: 'Task reset',
      deleted: 'Task deleted',
      deleteFailed: 'Delete task failed',
      deleteFailedRetry: 'Delete task failed, please try again later',
      intervene: 'Human intervention started, task continues',
    },
    filter: {
      tagsLabel: 'Result Tags:',
      tagsPlaceholder: 'Select result tags',
      timeLabel: 'Time Filter:',
      rangeSeparator: 'to',
      startPlaceholder: 'Start Time',
      endPlaceholder: 'End Time',
      clearFilters: 'Clear Filters',
      clearTimeFilter: 'Clear Time Filter',
      clearTagFilter: 'Clear Tag Filter',
      clearAllFilters: 'Clear All Filters',
      reset: 'Reset',
    },
    table: {
      id: 'ID',
      executeTime: 'Execute Time',
      hasError: 'Abnormal',
      hasErrorTrue: 'Abnormal',
      hasErrorFalse: 'Normal',
      duration: 'Duration',
      resultTags: 'Tags',
      conversation: 'Conversation',
      conversationId: 'Conversation ID:',
      conversationTip: 'Tip:',
      conversationTipText: 'Click to open conversation detail',
      viewConversation: 'View',
      none: 'None',
      report: 'Report',
      viewReport: 'View Report',
      delete: 'Delete',
      deleteRecord: 'Delete Execution Record',
      deleteRecordConfirm: 'Delete this execution record?',
      recordDeleted: 'Execution record deleted',
      downloadFile: 'Download File',
      noErrorMessage: 'No error message',
      reportIdNotExists: 'Report ID does not exist',
      reportContentEmpty: 'Report content is empty',
      getReportFailed: 'Failed to get report content',
      getReportFailedRetry: 'Failed to get report content, please try again later',
      executionStatus: {
        success: 'Normal',
        error: 'Call Error',
        failed: 'Execution Error',
        humanIntervention: 'Human Intervention',
        initial: 'Initial',
      },
      viewReportTitle: 'View Report',
      errorInfoTitle: 'Error Information',
    },
    resetDialog: {
      title: 'Reset Task',
      timeRange: 'Time Range',
      period: 'Period',
      customPlaceholder: 'Enter custom period description',
      tips: 'Granularity: 10 minutes, hour, day, week; or choose "Custom"',
      execCount: 'Execution Count',
      execCountPlaceholder: '0 means unlimited',
      execCountTips: 'Task stops when count satisfied within range',
      cancel: 'Cancel',
      confirmReset: 'Confirm Reset',
    },
    validate: {
      timeRange: 'Please select time range',
    },
    period: {
      every10m: 'Every 10 minutes',
      hourly: 'Hourly',
      daily: 'Daily',
      weekly: 'Weekly',
    },
    viewConversation: 'View Conversation',
    agentCannotUse: 'Agent cannot be used',
    cannotGetTaskData: 'Unable to get task data, please try again later',
    cannotGetAgentId: 'Unable to get agent ID, please try again later',
    conversationIdNotExists: 'Conversation ID does not exist',
    groupSummary: '{timeRange}, should run {total} times, actually ran {actual} times, {error} errors',
    periodEvery10Min: 'Every 10 minutes',
    periodHourly: 'Hourly',
    periodDaily: 'Daily',
    periodWeekly: 'Weekly',
  },
  feedbackDialog: {
    title: 'Feedback',
    rateLabel: '* Please rate our service',
    suggestionLabel: '* Your suggestion',
    placeholder: 'Please enter your valuable suggestion...',
    submitting: 'Submitting...',
    submit: 'Submit',
    submitFailed: 'Submit failed, please try again later',
  },
  platformFeedbackDialog: {
    titleExpert: 'Rate expert: {name}',
    titleSkill: 'Rate skill: {name}',
    defaultTarget: 'Current item',
    description: 'Feedback is sent to the admin platform and visible to managers only, for capability optimization and configuration.',
    typeLabel: 'Feedback type',
    ratingLabel: 'Star rating',
    contentLabel: 'Feedback content',
    contentPlaceholder: 'Describe your experience, issues, or changes you want managers to consider',
    contentHint: 'This content is not public and is only used to communicate with managers.',
    contentRequired: 'Please enter feedback content',
    submitting: 'Submitting...',
    submit: 'Submit to admin platform',
    types: {
      experience: 'User experience',
      capability: 'Capability suggestion',
      quality: 'Result quality',
      permission: 'Permission or configuration',
    },
    ratings: {
      5: '★★★★★ Very satisfied',
      4: '★★★★☆ Satisfied',
      3: '★★★☆☆ Neutral',
      2: '★★☆☆☆ Needs improvement',
      1: '★☆☆☆☆ Dissatisfied',
    },
  },
  notification: {
    tabs: {
      all: 'All',
      unread: 'Unread',
      read: 'Read',
    },
    actions: {
      markRead: 'Mark as Read',
      delete: 'Delete',
      markAllRead: 'Mark All Read',
      deleteAll: 'Delete All',
      close: 'Close',
    },
    table: {
      serialNumber: 'No.',
      title: 'Title',
      status: 'Status',
      priority: 'Priority',
      sender: 'Sender',
      time: 'Time',
      type: 'Type',
      selectAll: 'Select all',
      empty: 'No notifications',
    },
    status: {
      read: 'Read',
      unread: 'Unread',
    },
    priority: {
      normal: 'Normal',
      important: 'Important',
      urgent: 'Urgent',
    },
    type: {
      task: 'Task Notification',
      email: 'Email Notification',
      messageNotification: 'Message Notification',
      exception: 'Exception',
      message: 'Message',
      event: 'Event',
    },
    messages: {
      loading: 'Loading...',
      selectToMarkRead: 'Please select notifications to mark as read',
      markSuccess: 'Marked successfully',
      markFailed: 'Mark failed',
      selectToDelete: 'Please select notifications to delete',
      deleteSuccess: 'Deleted successfully',
      deleteFailed: 'Delete failed',
      fetchFailed: 'Failed to fetch notifications',
      confirmDelete: 'Are you sure to delete the selected notifications?',
      confirmMarkAllRead: 'Are you sure to mark all notifications as read?',
      confirmDeleteAll: 'Are you sure to delete all notifications?',
      tip: 'Tip',
    },
    detail: {
      title: 'Notification Details',
      basicInfo: 'Basic Information',
      content: 'Content',
      relatedInfo: 'Related Information',
      remark: 'Remarks',
      targetUsers: 'Target Users',
      fields: {
        id: 'Notification ID:',
        title: 'Title:',
        type: 'Type:',
        priority: 'Priority:',
        status: 'Status:',
        sender: 'Sender:',
        relatedId: 'Related ID:',
        relatedType: 'Related Type:',
      },
      defaultSender: 'System',
    },
    pagination: {
      total: 'Total',
      items: 'items',
      perPage: 'Per page',
      itemsUnit: 'items',
    },
  },
  login: {
    button: 'Login',
    title: 'Agent Workspace',
    form: {
      username: 'Please enter username',
      password: 'Please enter password',
      newPassword: 'Please enter new password',
      code: 'Please enter verification code',
      phone: 'Please enter phone number',
      phoneInvalid: 'Please enter a valid phone number',
      confirmPassword: 'Please confirm password',
      passwordMismatch: 'Passwords do not match',
      codeLength: 'Verification code must be 4 or 6 digits',
      passwordMinLength: 'Password must be at least 6 characters',
    },
    actions: {
      login: 'Login',
      register: 'Register',
      forgotPassword: 'Forgot Password',
      freeRegister: 'Free Register',
      backToLogin: 'Back to Login',
      hasAccount: 'Already have an account, ',
      getCode: 'Get Code',
      codeSent: 'Verification code sent successfully',
      loginSuccess: 'Login successful',
      registerSuccess: 'Registration successful',
      resetSuccess: 'Password reset successful',
      resetPassword: 'Reset Password',
      captchaFailed: 'Failed to get verification code, please try again',
      phoneRequired: 'Please enter phone number',
    },
  },
  logout: {
    title: 'Logout',
    confirmTitle: 'Confirm Logout?',
    confirmMessage: 'Logging out will not lose any data, you can still log in to this account.',
    confirmButton: 'Confirm Logout',
    cancelButton: 'Cancel',
    success: 'Logout successful',
  },
  chat: {
    retry: 'Retry',
    copy: 'Copy',
    like: 'Like',
    dislike: 'Dislike',
    speak: 'Speak',
    stopSpeak: 'Stop',
    more: 'More',
    scrollToBottom: 'Scroll to bottom',
    inputPlaceholder: 'Type a message',
    emptyTitle: 'How can I help you?',
    emptyDescription: 'Describe the work you want to complete. The assistant will use authorized knowledge, skills, and connectors as needed.',
    aiNote: 'AI-generated content is for reference only. Please verify important information.',
    copied: 'Copied to clipboard',
    copyOriginal: 'Copy original',
    likeSuccess: 'Liked',
    likeFailed: 'Like failed',
    dislikeSuccess: 'Disliked',
    dislikeFailed: 'Dislike failed',
    ttsNotSupported: 'Text to speech not supported',
    uploadingFile: 'Uploading file...',
    uploadingFiles: 'Uploading {count} files...',
  },
  agentSelector: {
    externalAgent: 'External Agent',
    qrCodeAlt: '{name} QR Code',
    qrCodeTip: 'Please scan the QR code with WeChat to access the external agent',
    welcomeImageAlt: 'Welcome Image',
    selectAgentTip: 'Please select an agent to start a conversation',
  },
  retrieverResources: {
    title: 'Referenced Files',
    paragraph: 'Paragraph {index}',
    position: 'Position:',
    documentId: 'Document ID:',
    segmentCount: 'Segment Count:',
    segmentId: 'Segment ID:',
  },
  deepThinking: {
    label: 'Deep Thinking',
  },
  welcomeText: {
    defaultUsername: 'I am Agent Workspace',
  },
  iconSelect: {
    user: 'User',
    building: 'Building',
    business: 'Business',
    device: 'Device',
    document: 'Document',
    system: 'System',
    others: 'Others',
    default: 'Default',
  },
  filesSelect: {
    missingAgentId: 'Missing Agent ID',
    selectAgentFirst: 'Please select or enter an agent conversation first to upload files',
    uploadFailed: 'File upload failed',
    uploadButton: 'Upload Files or Images',
    fixUploadError: 'Some files failed to upload. Remove them and try again.',
    dropToUpload: 'Drop files to upload',
    maxFiles: 'You can upload up to {count} files',
  },
  difyWorkflow: {
    uploadLocal: 'Upload from local',
    pasteFileLink: 'Paste file URL',
    useAttachmentGuide: 'Please click the attachment button to enter Dify workflow processing',
    remoteUrlPlaceholder: 'Enter a publicly accessible file URL',
    invalidUrl: 'URL must start with http:// or https://',
    remove: 'Remove',
    uploading: 'Uploading…',
    waitUpload: 'Please wait until uploads finish',
    fixUploadError: 'Some files failed to upload. Remove them and try again.',
    fillRequiredFile: 'Please upload: {name}',
    fillRequiredText: 'Please enter a message',
    noFileInputs: 'No file input items are configured for this workflow.',
    startRun: 'Start Run',
  },
  agentHeaderExtra: {
    defaultCategory: 'Default Category',
    supportsTask: 'Task Capable',
    detail: 'Details',
    newChat: 'New Chat',
    newTask: 'New Task',
    feedback: 'Feedback',
    detailTitle: 'Agent Details',
    version: 'Version',
    currentStatus: 'Status',
    status: {
      available: 'Available',
      disabled: 'Disabled',
      offline: 'Offline',
    },
    intro: 'Introduction',
    noDescription: 'No description',
    taskCapability: 'Task Capability',
    scheduledSupported: 'Scheduled tasks supported',
    scheduledUnsupported: 'Scheduled tasks not supported',
    stats: {
      instantChat: 'Instant Chats:',
      taskExec: 'Task Executions:',
    },
    times: 'times',
    feedbackSuccess: 'Feedback submitted successfully, thank you for your suggestion!',
    feedbackFailed: 'Submission failed, please try again later',
    selectAgentFirst: 'Please select an agent first',
  },
  pageTitle: {
    chat: 'Agent Workspace',
    login: 'Login',
    notification: 'Notifications',
    taskDetail: 'Task Detail',
    notFound: '404 Page',
    forbidden: '403 Page',
  },
};
